/**
 * Screen 10 — orders.
 *
 * The prototype's sheet: pick a supplier, pick an order type, step the
 * quantities, watch the total and the limit banner move. What differs is that
 * this one is real — the proposal comes from the venue's own stock levels
 * against the supplier's par, the limit comes from the org's settings, and
 * sending it writes an order and puts it on the queue.
 *
 * The arithmetic is `reviewOrder` from @baros/core, run here as the person
 * types and again by the API on submit. Two implementations of a spending
 * limit is how a bar discovers at the till that the screen was optimistic.
 *
 * The banner tells the truth before the button is pressed: under the
 * supplier's minimum it will be refused, over the person's limit it will stop
 * and wait for a manager, and within both it goes.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DEFAULT_ORDER_LIMITS,
  ORDER_TYPES,
  ORDER_TYPE_NOTES,
  reviewOrder,
  type OrderLine,
  type OrderStatus,
  type OrderType,
} from "@baros/core";
import { ApiError } from "../api.js";
import { useApp } from "../context.js";
import { money, shortDate } from "../format.js";
import type { ScreenProps } from "../screens.js";
import {
  ordersApi,
  type Order,
  type OrderList,
  type Supplier,
  type Suggestion,
} from "../suppliers/api.js";
import { channelLabel } from "../suppliers/SuppliersScreen.js";
import "../suppliers/suppliers.css";

const dollars = (cents: number) => money(cents / 100);

const STATUS_LABEL: Readonly<Record<OrderStatus, string>> = {
  draft: "Draft",
  submitted: "Waiting on approval",
  approved: "Approved",
  queued: "Queued to send",
  sent: "Sent",
  received: "Received · invoiced",
  cancelled: "Cancelled",
};

const STATUS_COLOUR: Readonly<Record<OrderStatus, string>> = {
  draft: "var(--t2)",
  submitted: "var(--warn)",
  approved: "var(--pos)",
  queued: "var(--pos)",
  sent: "var(--pos)",
  received: "var(--t2)",
  cancelled: "var(--neg)",
};

const orderTypeOr = (value: string | undefined): OrderType =>
  (ORDER_TYPES as readonly string[]).includes(value ?? "")
    ? (value as OrderType)
    : ORDER_TYPES[0];

export function OrdersScreen({ params }: ScreenProps) {
  const app = useApp();
  const { token, venueId } = app;
  const canCreate = app.me.can["order:create"];
  const canApprove = app.me.can["order:approve"];

  const [suppliers, setSuppliers] = useState<readonly Supplier[]>([]);
  const [list, setList] = useState<OrderList | null>(null);
  const [supplierId, setSupplierId] = useState<string | null>(params.supplierId ?? null);
  const [type, setType] = useState<OrderType>(orderTypeOr(params.type));
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  /** Quantities the person has changed, by item name. The proposal is the rest. */
  const [qty, setQty] = useState<ReadonlyMap<string, number>>(new Map());
  const [sent, setSent] = useState<Order | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const [sup, orders] = await Promise.all([
        ordersApi.suppliers(token),
        ordersApi.orders(token, venueId),
      ]);
      setSuppliers(sup.suppliers);
      setList(orders);
      setSupplierId((current) => current ?? sup.suppliers[0]?.supplierId ?? null);
    } catch (err) {
      setError(message(err));
    } finally {
      setLoading(false);
    }
  }, [token, venueId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The proposal for the chosen supplier and type.
   *
   * Refetched when either changes, and the hand-set quantities are cleared
   * with it: a number typed against a weekly standing order means nothing once
   * the sheet becomes a function order for twice the volume.
   */
  useEffect(() => {
    if (!venueId || !supplierId) return;
    let live = true;
    setQty(new Map());
    setSent(null);
    ordersApi
      .suggest(token, venueId, supplierId, type)
      .then((got) => {
        if (live) setSuggestion(got);
      })
      .catch((err: unknown) => {
        if (live) setError(message(err));
      });
    return () => {
      live = false;
    };
  }, [token, venueId, supplierId, type]);

  const supplier = suppliers.find((s) => s.supplierId === supplierId);

  /** The sheet as it stands: the proposal, with anything hand-set on top. */
  const lines: readonly OrderLine[] = useMemo(() => {
    if (!suggestion) return [];
    return suggestion.lines.map((line) => ({
      ingredientId: line.ingredientId,
      name: line.name,
      pack: line.pack,
      qty: qty.get(line.name) ?? line.qty,
      unitCostCents: line.unitCostCents,
    }));
  }, [suggestion, qty]);

  /**
   * The same review the API will run on submit.
   *
   * Run locally so the banner and the total move as the stepper is tapped.
   * When the limits have not loaded yet the defaults stand in — they are the
   * prototype's figures, and a banner that is briefly generous is better than
   * one that flickers.
   */
  const review = useMemo(
    () =>
      reviewOrder({
        lines,
        role: list?.limitRole ?? "bartender",
        limits: list?.limits ?? DEFAULT_ORDER_LIMITS,
        minimumExGstCents: supplier?.minimumExGstCents ?? 0,
      }),
    [lines, list, supplier],
  );

  const setLineQty = (name: string, next: number) => {
    setQty((current) => new Map(current).set(name, Math.max(0, next)));
    setSent(null);
  };

  /**
   * Write the order and submit it in one go.
   *
   * The prototype had one button, and so does this: what the bar is doing is
   * placing an order, not saving a draft and then remembering to send it. The
   * draft still exists as an item — it is what the API validates against and
   * what an approver later opens — but nobody has to think about it.
   */
  const send = async () => {
    if (!venueId || !supplierId) return;
    setBusy(true);
    setError(null);
    try {
      const created = await ordersApi.createOrder(token, venueId, {
        supplierId,
        type,
        lines: lines.filter((l) => l.qty > 0),
      });
      const submitted = await ordersApi.submitOrder(token, venueId, created.order.orderId);
      setSent(submitted.order);
      await load();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  const approve = async (order: Order) => {
    if (!venueId) return;
    setBusy(true);
    setError(null);
    try {
      await ordersApi.approveOrder(token, venueId, order.orderId);
      await load();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (order: Order) => {
    if (!venueId) return;
    setBusy(true);
    setError(null);
    try {
      await ordersApi.cancelOrder(token, venueId, order.orderId);
      await load();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  if (!venueId) {
    return (
      <div className="pad">
        <div className="msg err">
          You are not assigned to a venue, so there is nothing to order for.
        </div>
      </div>
    );
  }

  const waiting = (list?.orders ?? []).filter((o) => o.status === "submitted");
  const gstCents = review.subtotalIncGstCents - review.subtotalExGstCents;
  const limitCents = list?.limitCents ?? null;

  return (
    <div className="screen">
      {error && (
        <div style={{ padding: "12px 20px 0" }}>
          <div className="msg err">{error}</div>
        </div>
      )}

      <div className="split">
        <section className="pane">
          <div className="ord-head">
            <div className="label">Place an order</div>
            <div className="ord-suppliers">
              {suppliers.map((s) => (
                <button
                  key={s.supplierId}
                  className="btn"
                  aria-pressed={s.supplierId === supplierId}
                  onClick={() => setSupplierId(s.supplierId)}
                >
                  {s.name}
                </button>
              ))}
              {suppliers.length === 0 && !loading && (
                <span className="label">No suppliers to order from yet.</span>
              )}
            </div>
          </div>

          <div className="ord-types">
            {ORDER_TYPES.map((t) => (
              <button key={t} className="btn" aria-pressed={type === t} onClick={() => setType(t)}>
                {t}
              </button>
            ))}
            <span className="spacer" />
            <div className="ord-note">{ORDER_TYPE_NOTES[type]}</div>
          </div>

          <div className="scroll" style={{ padding: "14px 20px" }}>
            {!suggestion && <div className="pad label">Working out what is short…</div>}

            {suggestion && (
              <div className="table">
                {suggestion.lines.map((line, i) => {
                  const current = lines[i]?.qty ?? 0;
                  return (
                    <div className="ord-line" key={line.name}>
                      <span className="nm">
                        <span className="n">{line.name}</span>
                        <span className="s">
                          {line.pack} · {dollars(line.unitCostCents)} ex gst
                        </span>
                      </span>
                      <button
                        className="btn dashed"
                        disabled={!canCreate}
                        onClick={() => setLineQty(line.name, line.qty)}
                        title="Take the proposed quantity"
                      >
                        {line.heldUnits === null
                          ? `Par ${line.par} ${line.unit} · never counted → ${line.qty}`
                          : `Par ${line.par} ${line.unit} · ${line.heldUnits.toFixed(1)} held → ${line.qty}`}
                      </button>
                      <button
                        className="step"
                        aria-label={`One less ${line.name}`}
                        disabled={!canCreate || current === 0}
                        onClick={() => setLineQty(line.name, current - 1)}
                      >
                        −
                      </button>
                      <span className={`qty ${current > 0 ? "set" : ""}`}>{current}</span>
                      <button
                        className="step"
                        aria-label={`One more ${line.name}`}
                        disabled={!canCreate}
                        onClick={() => setLineQty(line.name, current + 1)}
                      >
                        +
                      </button>
                      <span className={`tot ${current > 0 ? "set" : ""}`}>
                        {current > 0 ? dollars(current * line.unitCostCents) : "—"}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="label" style={{ marginTop: 12, lineHeight: 1.7 }}>
              {supplier
                ? `Priced and ordered by the pack · minimum order ${
                    supplier.minimumExGstCents
                      ? `${dollars(supplier.minimumExGstCents)} ex GST`
                      : "none"
                  }${review.subtotalExGstCents ? ` · ${dollars(review.subtotalExGstCents)} ex GST so far` : ""}`
                : ""}
            </div>

            {(list?.orders.length ?? 0) > 0 && (
              <>
                <div className="label" style={{ margin: "22px 0 10px" }}>
                  This venue&rsquo;s orders
                </div>
                <div className="table">
                  {list?.orders.map((order) => (
                    <div className="ord-hist" key={order.orderId}>
                      <span className="nm">
                        {order.supplierName ?? order.supplierId}
                        <span className="s">
                          {order.type} · {order.lines.length} lines ·{" "}
                          {shortDate(order.createdAt)}
                        </span>
                      </span>
                      <span className="st" style={{ color: STATUS_COLOUR[order.status] }}>
                        {STATUS_LABEL[order.status]}
                      </span>
                      <span className="amt">{dollars(order.subtotalIncGstCents)}</span>
                      {order.status === "submitted" && canApprove && (
                        <button
                          className="btn sm"
                          disabled={busy}
                          onClick={() => void approve(order)}
                        >
                          Approve
                        </button>
                      )}
                      {(order.status === "draft" || order.status === "submitted") && (
                        <button
                          className="btn sm danger"
                          disabled={busy}
                          onClick={() => void cancel(order)}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        <aside className="ord-aside">
          <div className="block">
            <div className="label">Order to</div>
            <div className="to">{supplier?.name ?? "—"}</div>
            <div className="via">
              {supplier ? channelLabel(supplier) : "—"}
              <br />
              {supplier
                ? `${supplier.terms ?? "Terms unset"} · delivers ${supplier.cutoff ?? "on request"}`
                : ""}
            </div>
          </div>

          <div className="block ord-sum">
            <div className="r">
              <span>Subtotal ex GST</span>
              <span>{dollars(review.subtotalExGstCents)}</span>
            </div>
            <div className="r">
              <span>GST</span>
              <span>{dollars(gstCents)}</span>
            </div>
            <div className="total">
              <span className="label">Total</span>
              <span className="v">{dollars(review.subtotalIncGstCents)}</span>
            </div>
            <div className="label" style={{ letterSpacing: "0.1em" }}>
              Your limit ·{" "}
              {list?.limitRole
                ? `${list.limitRole} ${
                    limitCents === null || limitCents >= 10_000_000
                      ? "no limit"
                      : dollars(limitCents)
                  }`
                : "no ordering role here"}
            </div>
          </div>

          <div className="scroll block" style={{ borderBottom: 0 }}>
            <div className="label">Waiting on approval</div>
            {waiting.length === 0 && (
              <div className="label" style={{ marginTop: 10, lineHeight: 1.8 }}>
                Nothing waiting.
                <br />
                An order over your limit stops here
                <br />
                until somebody senior clears it.
              </div>
            )}
            {waiting.map((order) => (
              <div className="chip" key={order.orderId} style={{ marginTop: 10, width: "100%" }}>
                <span className="dot" style={{ background: "var(--warn)" }} />
                <span style={{ flex: 1 }}>
                  {order.supplierName ?? order.supplierId} ·{" "}
                  {dollars(order.subtotalIncGstCents)}
                </span>
                {canApprove && (
                  <button
                    className="btn sm"
                    disabled={busy}
                    onClick={() => void approve(order)}
                  >
                    Approve
                  </button>
                )}
              </div>
            ))}

            {sent && (
              <div className="msg ok" style={{ marginTop: 14, fontSize: 12, lineHeight: 1.7 }}>
                {sent.status === "queued"
                  ? `Queued to ${supplier ? channelLabel(supplier).toLowerCase() : "the supplier"}. ` +
                    `${dollars(sent.subtotalIncGstCents)} inc GST.`
                  : `Submitted and waiting on approval. ${dollars(sent.subtotalIncGstCents)} inc GST.`}
              </div>
            )}
          </div>

          <div className="ord-send">
            <div className={`ord-banner ${bannerIsWarning(review) ? "warn" : ""}`}>
              {banner(review, supplier, limitCents)}
            </div>
            <button
              className={`btn primary ${!review.withinLimit && review.canSubmit ? "flag" : ""}`}
              disabled={!canCreate || busy || !review.canSubmit}
              onClick={() => void send()}
            >
              {review.underMinimum
                ? "Held · under minimum"
                : !review.withinLimit
                  ? "Send and flag for approval"
                  : "Send order"}
            </button>
            {!canCreate && (
              <span className="label">Your role does not allow raising an order.</span>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

type Review = ReturnType<typeof reviewOrder>;

const bannerIsWarning = (review: Review) => review.underMinimum || !review.withinLimit;

/**
 * What the banner says, in the prototype's voice: the consequence of pressing
 * the button, in money, before it is pressed.
 */
function banner(
  review: Review,
  supplier: Supplier | undefined,
  limitCents: number | null,
): string {
  if (review.outcome === "empty") return "Set quantities to start an order.";

  if (review.underMinimum) {
    return (
      `${dollars(review.subtotalExGstCents)} ex GST is under ` +
      `${supplier?.name ?? "the supplier"}'s ` +
      `${dollars(supplier?.minimumExGstCents ?? 0)} minimum. Add ` +
      `${dollars(review.minimumShortfallCents)} more or the order is held, not sent.`
    );
  }

  if (!review.withinLimit) {
    const who = review.approverRoles.length
      ? `a ${review.approverRoles.join(" or ")}`
      : "somebody with a limit nobody currently holds";
    return (
      `${dollars(review.subtotalIncGstCents)} is over the ` +
      `${dollars(limitCents ?? review.limitCents)} you can send unattended. ` +
      `Sending it parks the order for ${who} to approve before it leaves.`
    );
  }

  return (
    "Within your limit and over the minimum. This is approved on send and " +
    `queued to ${supplier ? channelLabel(supplier).toLowerCase() : "the supplier"}.`
  );
}

function message(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : "The order could not be loaded";
}
